import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setUnauthorisedHandler } from '../api/client';
import type { OrgMembership, Session, User } from '../api/types';

/**
 * Who is signed in and which publisher they are working on.
 *
 * The session itself is an HttpOnly cookie, so the app cannot read it. It asks the API once on
 * load instead, and any 401 from anywhere clears this state, which is what pushes a stale tab
 * back to the sign in page.
 */

type Status = 'loading' | 'signed_in' | 'signed_out';

interface AuthValue {
  readonly status: Status;
  readonly user: User | null;
  readonly orgs: readonly OrgMembership[];
  readonly org: OrgMembership | null;
  selectOrg(orgId: string): void;
  /** Ask the API who is signed in again, after something has changed the answer. */
  refresh(): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signUp(input: {
    orgName: string;
    brandName: string;
    firstName: string;
    /** Empty for somebody who goes by one name. The API stores it as an empty string. */
    lastName: string;
    email: string;
    password: string;
  }): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

const ORG_STORAGE_KEY = 'quizee.org_id';

function readStoredOrgId(): string | null {
  try {
    return window.localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeOrgId(orgId: string | null): void {
  try {
    if (orgId === null) window.localStorage.removeItem(ORG_STORAGE_KEY);
    else window.localStorage.setItem(ORG_STORAGE_KEY, orgId);
  } catch {
    // Storage can be blocked. The choice then lasts for this visit only, which is fine.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [orgs, setOrgs] = useState<readonly OrgMembership[]>([]);
  const [orgId, setOrgId] = useState<string | null>(readStoredOrgId);

  const applySession = useCallback((session: Session) => {
    setUser(session.user);
    // Older API deployments return membership ids and roles without publisher names.
    setOrgs(session.orgs.map((org) => ({ ...org, name: org.name ?? 'Publisher' })));
    setStatus('signed_in');
    setOrgId((current) => {
      if (current !== null && session.orgs.some((org) => org.org_id === current)) return current;
      const first = session.orgs[0]?.org_id ?? null;
      storeOrgId(first);
      return first;
    });
  }, []);

  const clearSession = useCallback(() => {
    setUser(null);
    setOrgs([]);
    setStatus('signed_out');
  }, []);

  const refresh = useCallback(async () => {
    applySession(await api.me());
  }, [applySession]);

  useEffect(() => {
    setUnauthorisedHandler(clearSession);
    return () => setUnauthorisedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((session) => {
        if (!cancelled) applySession(session);
      })
      .catch(() => {
        if (!cancelled) clearSession();
      });
    return () => {
      cancelled = true;
    };
  }, [applySession, clearSession]);

  const value = useMemo<AuthValue>(() => {
    const org = orgs.find((candidate) => candidate.org_id === orgId) ?? orgs[0] ?? null;
    return {
      status,
      user,
      orgs,
      org,
      selectOrg(next: string) {
        storeOrgId(next);
        setOrgId(next);
      },
      refresh,
      async signIn(email: string, password: string) {
        applySession(await api.login(email, password));
      },
      async signUp(input) {
        const created = await api.signup(input);
        applySession({
          user: created.user,
          orgs: [{ org_id: created.org.org_id, name: created.org.name, role: 'owner' }],
        });
      },
      async signOut() {
        try {
          await api.logout();
        } finally {
          storeOrgId(null);
          setOrgId(null);
          clearSession();
        }
      },
    };
  }, [applySession, clearSession, orgId, orgs, refresh, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth used outside AuthProvider');
  return value;
}
